import * as Sentry from "@sentry/browser";
import { Dictionary, groupBy } from "lodash";
import { FormattedMessage } from "react-intl";
import { AppContextState } from "../AppContextProvider";
import { Typenames } from "../constants/constants";
import { FileAssociationsFragment, Theme } from "../types.g";

/**
 * Looking up where something is used must never stop the user deleting it.
 * An unhandled rejection here aborts the click handler before the confirm
 * dialog opens, so the delete button does nothing at all and says nothing
 * either. Report the failure and fall back to the plain confirmation.
 * See GRWBKK-784.
 */
export const tolerateLookupFailure = <T,>(
  lookup: Promise<T>,
): Promise<T | null> =>
  lookup.catch((error) => {
    Sentry.captureException(error);
    return null;
  });

export type UsedPlace = Dictionary<
  ({
    position: any;
    isDraft: any;
    name: any;
    id: any;
  } | null)[]
>;

/**
 * An association names a single entity (channel, playlist, cast, canvas or
 * screen) that uses the content. Every other relation on the node is null.
 *
 * The exception is an association the user may not resolve: association rows
 * are readable org-wide, but the entity they point at is only readable in
 * spaces the user has access to, so *every* relation comes back null. Those
 * nodes carry no name we can show — `hasInaccessiblePlaces` reports them.
 */
const getAssociatedPlace = (association: FileAssociationsFragment) => {
  const key = Object.keys(association).find(
    (name) => name !== "__typename" && association[name],
  );
  return key ? association[key] : undefined;
};

/**
 * Whether any place using this content is hidden from the current user.
 *
 * Deliberately a yes/no rather than a count: one channel or playlist with an
 * unpublished draft produces two association rows (see the de-dup below), and
 * a hidden node carries nothing we could de-dup on, so any number we reported
 * would overstate how many distinct items are affected.
 */
export const hasInaccessiblePlaces = (
  fileAssociations?: FileAssociationsFragment[],
): boolean =>
  (fileAssociations ?? []).some(
    (association) => !getAssociatedPlace(association),
  );

export const getTheUsedPlaceObject = (
  fileAssociations: FileAssociationsFragment[],
): UsedPlace => {
  const listOfPlacesUsedMedia = fileAssociations
    .map(getAssociatedPlace)
    .filter(Boolean)
    .map((place) => {
      const position =
        place.__typename === Typenames.AppInstance
          ? "Canvas"
          : place.__typename; // only canvas app can be attach media file
      return {
        position,
        isDraft: place.draft,
        name:
          position !== Typenames.Cast
            ? place.name
            : place.screensByCastId.nodes
                .map((screen) => screen.name)
                .join(", "),
        id: place.id,
        coverData: place.coverData,
        fileByCoverImageId: place.fileByCoverImageId,
        color: place.color,
      };
    });

  // if there are 2 items from fileAssociations (one is published version and another one is a draft version) should remove the published version and always use the draft item only
  const draftIds = listOfPlacesUsedMedia
    .map((palce) => palce.isDraft?.id)
    .filter(Boolean);
  const publishedIds = listOfPlacesUsedMedia.map((place) => place?.id);
  const idsToRemove = publishedIds.filter((publishedId) =>
    draftIds.includes(publishedId),
  );

  const placesDetail = listOfPlacesUsedMedia
    .map((place) => {
      if (idsToRemove.includes(place.id)) {
        return null;
      }
      if (
        place.position === Typenames.Channel ||
        place.position === Typenames.Playlist
      ) {
        return {
          ...place,
          name: place.isDraft?.name ?? place.name,
        };
      }
      return place;
    })
    .filter((place) => place?.name)
    .filter(Boolean);
  const groupByPosition = groupBy(placesDetail, "position");
  return groupByPosition;
};

export const getTheUsedPlace = (
  fileAssociations?: FileAssociationsFragment[],
) => {
  if (fileAssociations) {
    const groupByPosition = getTheUsedPlaceObject(fileAssociations);
    const places = Object.entries(groupByPosition).map(([key, value]) => {
      return {
        position: key,
        name: value.map((i) => i?.name).join(", "),
      };
    });
    return places;
  }
  return [];
};

export const getUsedPlaceByFont = (themes: Pick<Theme, "id" | "name">[]) => {
  return [
    {
      position: "Theme",
      name: themes.map((theme) => theme.name).join(", "),
    },
  ];
};

export const getConfirmDeletStatus = async ({
  context,
  deleteMessage,
}: {
  context: AppContextState;
  deleteMessage: string | JSX.Element;
}) => {
  const settings = context.currentUser?.preferences?.settings;
  const shouldDeleteWithoutConfirmation =
    settings.delete_media_without_confirmation;

  if (!shouldDeleteWithoutConfirmation) {
    const { confirm, optionCheck } = await context.modal.confirm(
      deleteMessage,
      {
        confirm: (
          <FormattedMessage
            id="ui_component.common.label.move_to_trash"
            defaultMessage="Move to Trash"
          />
        ),
        option: true,
        isDanger: true,
      },
    );

    return { confirm, optionCheck };
  } else {
    return { confirm: true };
  }
};
